import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useFormikContext } from 'formik';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import { GetWeightsDoc } from '@minecraft.graphql-operations';
import { setFieldValueAndTouched } from '@minecraft.utils';
import ErrorMessage from '@minecraft.error-message';
import { useTranslation } from '@blocs.i18n';
import { emptyOption } from '../../../shared/config';
import { FilterData, UnitSystemNameEnum, RangeEnum } from '../../../shared/types';
import { generateSelectOptionsBasedOnField, useMeasure } from '../../../shared/utils';
import { Label, StyledCheckbox } from '../../../shared/styles';
import { RangeSelector } from '../../shared/components';

type Props = {
  weightRangeName: FilterData['name'];
  switchWeightToMetricName: FilterData['name'];
  weightMinName: FilterData['name'];
  weightMaxName: FilterData['name'];
  label: FilterData['label'];
};

const WeightRange: FC<Props> = ({
  weightRangeName,
  switchWeightToMetricName,
  weightMinName,
  weightMaxName,
  label: primaryLabel,
}) => {
  const { t } = useTranslation();
  const formikContext = useFormikContext();

  const switchToMetric = formikContext.values[weightRangeName][switchWeightToMetricName];
  const minValue = formikContext.values[weightRangeName][weightMinName];
  const maxValue = formikContext.values[weightRangeName][weightMaxName];

  const [getWeights, { data, error }] = useLazyQuery(GetWeightsDoc);

  const getWeightsBasedOnMetric = useCallback(
    (excludeInMetricUI) => {
      return excludeInMetricUI ? _filter(data?.weights, (weight) => weight.showInMetricUI) : data?.weights;
    },
    [data]
  );

  const handleSwitchToMetricChange = useCallback((): void => {
    const updatedSwitchToMetric = !switchToMetric;
    const updatedWeightOptions = generateSelectOptionsBasedOnField(
      getWeightsBasedOnMetric(updatedSwitchToMetric),
      updatedSwitchToMetric ? UnitSystemNameEnum.kilogramsRounded : UnitSystemNameEnum.pounds
    );

    setFieldValueAndTouched(`${weightRangeName}.${switchWeightToMetricName}`, updatedSwitchToMetric, formikContext);

    if (minValue.value) {
      const updatedMinValue = _find(updatedWeightOptions, { value: minValue.value }) || emptyOption;

      formikContext.setFieldValue(`${weightRangeName}.${weightMinName}`, updatedMinValue);
    }

    if (maxValue.value) {
      const updatedMaxValue = _find(updatedWeightOptions, { value: maxValue.value }) || emptyOption;

      formikContext.setFieldValue(`${weightRangeName}.${weightMaxName}`, updatedMaxValue);
    }
  }, [switchToMetric, minValue, maxValue, getWeightsBasedOnMetric]);

  useEffect((): void => {
    if (!data) {
      // fn from useLazyQuery doesn't return a promise
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getWeights();
    }
  }, [data]);

  const weightOptions = useMemo(() => {
    return generateSelectOptionsBasedOnField(
      getWeightsBasedOnMetric(switchToMetric),
      switchToMetric ? UnitSystemNameEnum.kilogramsRounded : UnitSystemNameEnum.pounds
    );
  }, [switchToMetric, getWeightsBasedOnMetric]);

  const label = `${primaryLabel} (${useMeasure({
    switchTo: switchToMetric,
    type: RangeEnum.weight,
  })})`;

  if (error) return <ErrorMessage error={error} />;

  return (
    <>
      <Label>{label}</Label>
      <RangeSelector
        saveAsOption
        showInImperial={!switchToMetric}
        rangeName={weightRangeName}
        minName={weightMinName}
        maxName={weightMaxName}
        options={weightOptions}
      />
      <StyledCheckbox
        isChecked={switchToMetric}
        name={`${weightRangeName}.${switchWeightToMetricName}`}
        label={t('common:form.title.switchToMetric')}
        onChange={handleSwitchToMetricChange}
      />
    </>
  );
};

export default memo(WeightRange);
